/**
 * Reports — landing page (Phase F α).
 * Shows 10 canned report tiles + link to Custom Report Builder + saved reports.
 *
 * Click a canned tile → /admin/reports/:reportKey (ReportRunner picks
 * the right definition and renders results inline).
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Calendar,
  ClipboardCheck,
  Truck,
  Building2,
  AlertTriangle,
  Beaker,
  Package,
  Clock,
  Users,
  ArrowDownUp,
  FileText,
} from "lucide-react";
import { listSavedReports, deleteSavedReport } from "@/lib/reportsApi";
import { useViewAsClient } from "@/lib/viewAsClient";
import { toast } from "sonner";

// Numeric-aware collator so "Report 2" sorts before "Report 10".
const NATURAL = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

export const CANNED_REPORTS = [
  { key: "bookings-by-site",     title: "Bookings by site",         icon: Building2,     description: "Booking volume grouped by site, with status breakdown." },
  { key: "bookings-by-status",   title: "Bookings by status",       icon: ClipboardCheck, description: "Pipeline view — draft → approved → completed." },
  { key: "bookings-by-direction",title: "Inbound vs outbound",      icon: ArrowDownUp,   description: "Direction split with site filter." },
  { key: "driver-utilisation",   title: "Driver utilisation",       icon: Truck,         description: "Bookings + total hours per assigned driver." },
  { key: "customer-traffic",     title: "Customer / carrier traffic",icon: Users,        description: "Sender → receiver frequency (by company)." },
  { key: "bay-utilisation",      title: "Bay utilisation %",        icon: Calendar,      description: "Booked hours ÷ open hours, per bay." },
  { key: "ncr-summary",          title: "NCR / Issues summary",     icon: AlertTriangle, description: "Issue type × status × site." },
  { key: "pallet-movement",      title: "Pallet movement",          icon: Package,       description: "IN / OUT volume by brand and counterparty." },
  { key: "sampling-outcomes",    title: "Sampling outcomes",        icon: Beaker,        description: "Flag count and sampled / skipped ratio." },
  { key: "lifecycle-late-missed",title: "Late / missed / no-show",  icon: Clock,         description: "Lifecycle stats for the period." },
];

export default function Reports() {
  const navigate = useNavigate();
  const { viewAsClientId } = useViewAsClient();
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data, error } = await listSavedReports(viewAsClientId || null);
    if (error) toast.error(error.message);
    const sorted = (data || []).slice().sort((a, b) => NATURAL.compare(a?.name || "", b?.name || ""));
    setSaved(sorted);
    setLoading(false);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await listSavedReports(viewAsClientId || null);
      if (cancelled) return;
      if (error) toast.error(error.message);
      const sorted = (data || []).slice().sort((a, b) => NATURAL.compare(a?.name || "", b?.name || ""));
      setSaved(sorted);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [viewAsClientId]);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Reports</h1>
          <p className="text-sm text-slate-500">
            Run a preconfigured report or build a custom one.
          </p>
        </div>
        <Button onClick={() => navigate("/admin/reports/custom")} data-testid="open-custom-builder">
          <FileText className="mr-2 h-4 w-4" /> Open custom builder
        </Button>
      </div>

      {/* Canned reports */}
      <section>
        <h2 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3">
          Preconfigured
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {CANNED_REPORTS.map(({ key, title, icon: Icon, description }) => (
            <Link key={key} to={`/admin/reports/${key}`} data-testid={`canned-report-${key}`}>
              <Card className="hover:border-slate-400 transition cursor-pointer h-full">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <div className="rounded-md bg-slate-900 text-white p-1.5">
                      <Icon className="h-4 w-4" />
                    </div>
                    <CardTitle className="text-base">{title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription>{description}</CardDescription>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      {/* Saved custom reports */}
      <section>
        <h2 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3">
          Saved reports
        </h2>
        {loading ? (
          <div className="text-sm text-slate-400">Loading…</div>
        ) : saved.length === 0 ? (
          <div className="text-sm text-slate-400 italic">
            No saved reports yet — open the custom builder to create one.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {saved.map((r) => (
              <Card key={r.id} data-testid={`saved-report-${r.id}`} className="hover:border-slate-400 transition">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base">{r.name}</CardTitle>
                    {r.is_shared && (
                      <span className="text-[10px] uppercase tracking-wider bg-slate-100 px-1.5 py-0.5 rounded">Shared</span>
                    )}
                  </div>
                  {r.description && <CardDescription>{r.description}</CardDescription>}
                </CardHeader>
                <CardContent className="flex justify-between items-center pt-0">
                  <Link to={`/admin/reports/custom?id=${r.id}`} className="text-sm text-blue-600 hover:underline">
                    Open
                  </Link>
                  <button
                    className="text-xs text-rose-600 hover:underline"
                    data-testid={`delete-saved-${r.id}`}
                    onClick={async () => {
                      if (!window.confirm(`Delete report "${r.name}"?`)) return;
                      const { error } = await deleteSavedReport(r.id);
                      if (error) toast.error(error.message);
                      else { toast.success("Deleted"); load(); }
                    }}
                  >
                    Delete
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
